import React from 'react'
import { useCartStore } from '../../../../../store/cartStore'
import { useUiStore } from '../../../../../store/uiStore'
import { useAddToCart } from '../../../../hooks/useAddToCart'
import styles from './styles.module.css'
import Text from '../../../../Text'
import ProgressiveImage from '../../../ProgressiveImage'
import { formatPrice } from '../../../../../utils/formats'
import TrashIcon from '../../../../icons/TrashIcon'
import { getShopItemPrice, isBundle } from '../../../../../utils/shopItemsAndCart'
import sendEvent, { buildRemoveFromCartEvent } from '../../../../../utils/analytics'
import { pushEcommerceEvent, buildEcommercePayload } from '../../../../../utils/dataLayer'
import AddIcon from '../../../../icons/AddIcon'
import SubtractIcon from '../../../../icons/checkout/SubtractIcon'
import { $neutralGray100 } from '../../../../../styles/colors/primitives'
import PreOrderTag from '../../../../ShopItem/PreOrderTag'
import { isPreOrder } from '../../../../../utils/shopItems'
import { isNullOrEmpty } from '../../../../../utils/arrays'
import { toSrc } from '../../../../../utils/imageSrc'

const cx = (...c) => c.filter(Boolean).join(' ')

function CartItem({ shopItem }) {
  // Cart line items + mutations now live in the Zustand cart store (Bucket C); the
  // panel-visibility flag lives on the Zustand ui store (Phase C2 part 1).
  const quantity = useCartStore((s) => s.cart[shopItem.id])
  const remove = useCartStore((s) => s.remove)
  const decrease = useCartStore((s) => s.decrease)
  const hideCart = useUiStore((s) => s.hideCart)
  const { checkoutPageAddToCart } = useAddToCart()

  const removeFromCart = (shopItem) => {
    remove(shopItem.id)
    sendEvent(buildRemoveFromCartEvent(shopItem))
    pushEcommerceEvent('remove_from_cart', buildEcommercePayload([shopItem]))

    // The store mutation is synchronous, so read the up-to-date cart to decide
    // whether the panel should auto-close (the last item was just removed).
    Object.keys(useCartStore.getState().cart).length === 0 && hideCart()
  }

  // No price in the current region => not purchasable here. Show it greyed out
  // with a notice + a working remove button, and let the panel block checkout.
  const unavailable = !getShopItemPrice(shopItem)

  return (
    <div className={cx(styles.item, unavailable && styles.itemUnavailable)}>
      <div className={styles.imageAndTitle}>
        <div className={cx(styles.imageWrapper, unavailable && styles.dimmed)}>
          {
            !isNullOrEmpty(shopItem.galleryImages)
              ? <ProgressiveImage {...shopItem.galleryImages[0]}/>
              : <ProgressiveImage
                image={shopItem.imageUrl && typeof shopItem.imageUrl === 'object' ? shopItem.imageUrl : undefined}
                src={toSrc(shopItem.imageUrl)}
                alt={shopItem.name}/>
          }
        </div>
        <div className={styles.textWrapper}>
          <div className={styles.productLineAndRemoveIcon}>
            <Text type="heading-10">VRS® DirectForce®</Text>
            <div className={styles.removeIconWrapper} onClick={() => removeFromCart(shopItem)}>
              <TrashIcon/>
            </div>
          </div>
          <Text type="heading-8" className={unavailable ? styles.dimmed : undefined}>{shopItem.name}</Text>
          { unavailable && <Text type="small" color="#b3261e">Not available in your region</Text> }
          <div className={styles.tags}>
            <div className={cx(styles.preOrderTagOverride, isPreOrder(shopItem) && styles.preOrderTagOverrideShow)}>
              <PreOrderTag show={isPreOrder(shopItem)}/>
            </div>
            <div className={cx(styles.bundledTag, isBundle(shopItem) && styles.bundledTagShow)}>
              <Text type="tiny">Bundle</Text>
            </div>
          </div>
        </div>
      </div>
      <div className={styles.priceAndQuantity}>
        { !unavailable && (
          <div className={cx(styles.quantity, shopItem.isAllowedMultipleQuantitiesInCart && styles.quantityShow)}>
            <div className={styles.icon} onClick={() => decrease(shopItem.id)}>
              <SubtractIcon/>
            </div>
            <div className={styles.quantityText}>
              <Text type="regular">{quantity}</Text>
            </div>
            <div className={styles.icon} onClick={() => checkoutPageAddToCart([shopItem.id])}>
              <AddIcon color={$neutralGray100}/>
            </div>
          </div>
        )}
        <div className={styles.price}>
          { unavailable
            ? <Text type="small" className={styles.dimmed}>Unavailable</Text>
            : <Text type="heading-8">{formatPrice(getShopItemPrice(shopItem))}</Text> }
        </div>
      </div>
    </div>
  )
}

export default CartItem
